import {
	ApplicationConfig,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import {
	provideHttpClient,
	withInterceptorsFromDi,
} from '@angular/common/http';
import {
	provideRouter,
	RouteReuseStrategy,
	withRouterConfig,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { LayoutModule } from '@angular/cdk/layout';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import {
	MatPaginatorIntl,
	MatPaginatorModule,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
	MatRadioModule,
	MAT_RADIO_DEFAULT_OPTIONS,
} from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule, MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { initializeAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { setPersistence, browserSessionPersistence } from 'firebase/auth';
import * as echarts from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import { environment } from 'src/environments/environment';
import { ENV, getEnv } from 'src/environment.provider';
import { AppLocalizedPaginator } from './components/i18n-paginator/i18n-paginator.injectable';
import { httpInterceptorProviders } from './http-interceptors';
import { AuthService } from './services/auth.service';
import { AnalyticsTrackingService } from './services/analytics-tracking.service';
import { BannerMessageService } from './services/banner-message.service';
import { EventService } from './services/event.service';
import { NavigationAuthGuardService } from './services/navigation-auth-guard.service';
import { NavigationService } from './services/navigation.service';
import { SelectActiveRoleGuardService } from './services/select-active-role-guard.service';
import { BaseRouteReuseStrategy, routes } from './app.routes';

export const appConfig: ApplicationConfig = {
	providers: [
		provideAnimations(),
		importProvidersFrom(
			FormsModule,
			ReactiveFormsModule,
			GoogleMapsModule,
			LayoutModule,
			ScrollingModule,
			MatButtonModule,
			MatButtonToggleModule,
			MatCardModule,
			MatCheckboxModule,
			MatChipsModule,
			MatNativeDateModule,
			MatDatepickerModule,
			MatDialogModule,
			MatExpansionModule,
			MatFormFieldModule,
			MatGridListModule,
			MatIconModule,
			MatInputModule,
			MatListModule,
			MatMenuModule,
			MatPaginatorModule,
			MatSelectModule,
			MatSidenavModule,
			MatSnackBarModule,
			MatSortModule,
			MatTableModule,
			MatTabsModule,
			MatToolbarModule,
			NgxEchartsModule.forRoot({ echarts }),
			MatProgressSpinnerModule,
			MatStepperModule,
			MatTooltipModule,
			MatSlideToggleModule,
			MatAutocompleteModule,
			MatProgressBarModule,
			MatRadioModule
		),
		provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
		{ provide: RouteReuseStrategy, useClass: BaseRouteReuseStrategy },
		provideFirebaseApp(() => initializeApp(environment.firebase)),
		provideAuth(() => {
			const auth = getAuth();
			setPersistence(auth, browserSessionPersistence);
			return auth;
		}),
		// Factory runs only when Analytics is first injected (after Accept All).
		provideAnalytics(() =>
			initializeAnalytics(getApp(), {
				config: {
					cookie_flags: 'SameSite=Lax;Secure',
				},
			})
		),
		provideHttpClient(withInterceptorsFromDi()),
		AuthService,
		BannerMessageService,
		EventService,
		NavigationAuthGuardService,
		NavigationService,
		SelectActiveRoleGuardService,
		{
			provide: MAT_TABS_CONFIG,
			useValue: {
				animationDuration: '0ms',
			},
		},
		httpInterceptorProviders,
		{
			provide: MatPaginatorIntl,
			useClass: AppLocalizedPaginator,
		},
		provideAppInitializer(() => {
			inject(AnalyticsTrackingService);
		}),
		{
			provide: ENV,
			useFactory: getEnv,
		},
		{
			provide: MAT_RADIO_DEFAULT_OPTIONS,
			useValue: { color: 'primary' },
		},
	],
};
